import { T } from "../tokens";

export interface SoundBarsProps {
  /** Whether the audio is currently playing. When false, the ripples
   *  freeze and the center dot dims. */
  playing: boolean;
  /** Total width in px. Default 88. Kept for callsite API compatibility
   *  with the previous bar-array version. */
  width?: number;
  /** Total height in px. Default 44. */
  height?: number;
  /** Ink color. Default ink-700 strokes, ink-900 center dot. */
  color?: string;
}

/**
 * SoundBars — "audio is playing" indicator.
 *
 * Day 14b (2026-05-26) redesign: the previous nine-bar EQ-style row was
 * read by users as a volume visualiser, which we couldn't honestly back
 * (Web Audio AnalyserNode integration on iOS proved too fragile — see
 * the chain of fixes that ended at commit a9d77a6). Renaming the
 * vocabulary entirely: a center ink dot with three concentric ripples
 * radiating outward, like a stone dropped in still water. Reads as
 * "this sound is travelling", with no implied amplitude meaning. Echoes
 * the existing ruifang-ripple keyframe used on map pins.
 *
 * The animation is pure SVG <animate>; no JS rAF, no Web Audio, nothing
 * the browser can break. When `playing` is false the rings simply
 * unmount and the center dot dims — clean pause/resume every time.
 *
 * Component name stays `SoundBars` so every existing import keeps
 * working with no callsite changes.
 */
export function SoundBars({
  playing,
  width = 88,
  height = 44,
  color = T.ink700,
}: SoundBarsProps) {
  const cx = width / 2;
  const cy = height / 2;
  return (
    <svg
      width={width}
      height={height}
      aria-hidden
      style={{ overflow: "visible" }}
    >
      <circle
        cx={cx}
        cy={cy}
        r={2}
        fill={T.ink900}
        opacity={playing ? 1 : 0.4}
        style={{
          transition: "opacity var(--dur-fade, 560ms) var(--ease-mist)",
        }}
      />
      {playing &&
        [0, 1, 2].map((i) => (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={3}
            fill="none"
            stroke={color}
            strokeWidth="0.75"
          >
            <animate
              attributeName="r"
              from="3"
              to="32"
              dur="3.6s"
              begin={`${-i * 1.2}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              from="0.55"
              to="0"
              dur="3.6s"
              begin={`${-i * 1.2}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}
    </svg>
  );
}
